'use client';

import { type Dispatch, type RefObject, type SetStateAction, useEffect } from 'react';
import { flushSync } from 'react-dom';

import { getSeoResolveReq } from '@/features/seo-listing/api';
import { isPermalinkPath } from '@/features/seo-listing/consts';
import type { SeoResolveData } from '@/features/seo-listing/types';
import {
  applySeoResolveDocumentMeta,
  mergeListingFilters,
  parseListingGetUrl,
} from '@/features/seo-listing/utils';
import type { FilterAction, FilterState } from '../store';
import {
  buildFilterReloadContext,
  buildPopstateIdentityUpdates,
  type FilterReloadContext,
  getBrowserPathname,
  hasListingIdentityUpdates,
  type ListingIdentityFilters,
  listingIdentityFromFilterState,
  listingIdentityFromParsedFilters,
} from '../utils';

interface UseListingPopstateSyncParams {
  filterStateRef: RefObject<FilterState>;
  filterIdentityByPathRef: RefObject<Map<string, ListingIdentityFilters>>;
  filterContextByPathRef: RefObject<Map<string, FilterReloadContext>>;
  isPopstateNavigationRef: RefObject<boolean>;
  dispatch: Dispatch<FilterAction>;
  setSeoPage: Dispatch<SetStateAction<SeoResolveData | null>>;
  setSeoPageTitle: Dispatch<SetStateAction<string | null>>;
  setListingPathEpoch: Dispatch<SetStateAction<number>>;
  syncRelatedFilterOptions: (context: FilterReloadContext) => void;
  syncStateFromUrl: () => boolean;
  restoreCatalogSeoPage: () => void;
}

/**
 * Browser Back/Forward handling: re-reads filters from the URL, restores
 * remembered listing identity, refreshes facet options and SEO chrome.
 */
export const useListingPopstateSync = ({
  filterStateRef,
  filterIdentityByPathRef,
  filterContextByPathRef,
  isPopstateNavigationRef,
  dispatch,
  setSeoPage,
  setSeoPageTitle,
  setListingPathEpoch,
  syncRelatedFilterOptions,
  syncStateFromUrl,
  restoreCatalogSeoPage,
}: UseListingPopstateSyncParams): void => {
  useEffect(() => {
    const rememberIdentityFromResolvedPath = async (path: string): Promise<void> => {
      const resolveResult = await getSeoResolveReq({ path });
      if (resolveResult.status !== 'ok') {
        return;
      }
      if (getBrowserPathname() !== path) {
        return;
      }

      setSeoPage(resolveResult.data);
      setSeoPageTitle(resolveResult.data.title ?? null);
      applySeoResolveDocumentMeta(resolveResult.data);

      const mergedFilters = mergeListingFilters(
        parseListingGetUrl(resolveResult.data.listingGetUrl),
        parseListingGetUrl(`${window.location.pathname}${window.location.search}`),
      );
      const snapshot = listingIdentityFromParsedFilters(mergedFilters);
      filterIdentityByPathRef.current.set(path, snapshot);

      const identityUpdates = buildPopstateIdentityUpdates(
        path,
        snapshot,
        listingIdentityFromFilterState({
          selectedFandoms: filterStateRef.current.selectedFandoms,
          selectedCharacters: filterStateRef.current.selectedCharacters,
          selectedTags: filterStateRef.current.selectedTags,
          selectedPairingCharacters: filterStateRef.current.selectedPairingCharacters,
        }),
      );
      if (hasListingIdentityUpdates(identityUpdates)) {
        flushSync(() => {
          dispatch({ type: 'SYNC_FROM_URL', payload: identityUpdates });
        });
      }

      syncRelatedFilterOptions(
        buildFilterReloadContext(path, {
          selectedFandoms: filterStateRef.current.selectedFandoms,
          selectedCharacters: filterStateRef.current.selectedCharacters,
          selectedTags: filterStateRef.current.selectedTags,
          selectedUniverses: filterStateRef.current.selectedUniverses,
          selectedPairingCharacters: filterStateRef.current.selectedPairingCharacters,
        }),
      );
    };

    const handlePopState = (): void => {
      isPopstateNavigationRef.current = true;
      const rememberedPath = getBrowserPathname();
      const didSyncFilters = syncStateFromUrl();

      let didSyncIdentity = false;
      const rememberedIdentity = filterIdentityByPathRef.current.get(rememberedPath);
      if (rememberedIdentity) {
        const identityUpdates = buildPopstateIdentityUpdates(
          rememberedPath,
          rememberedIdentity,
          listingIdentityFromFilterState({
            selectedFandoms: filterStateRef.current.selectedFandoms,
            selectedCharacters: filterStateRef.current.selectedCharacters,
            selectedTags: filterStateRef.current.selectedTags,
            selectedPairingCharacters: filterStateRef.current.selectedPairingCharacters,
          }),
        );
        if (hasListingIdentityUpdates(identityUpdates)) {
          didSyncIdentity = true;
          flushSync(() => {
            dispatch({ type: 'SYNC_FROM_URL', payload: identityUpdates });
          });
        }
      }

      // Query/identity sync dispatches consume the flag in the URL effect.
      // If Back changed nothing in filter state, clear it now so the next
      // status/rating/tag edit still pushStates a history entry.
      if (!didSyncFilters && !didSyncIdentity) {
        isPopstateNavigationRef.current = false;
      }

      // Path-encoded identity often leaves selectedFandoms unchanged on
      // popstate (syncStateFromUrl skips those keys). Force a listing refetch
      // so the grid follows the URL even when filter ids were already "wrong".
      setListingPathEpoch((epoch) => epoch + 1);

      const rememberedContext = filterContextByPathRef.current.get(rememberedPath);
      if (rememberedContext) {
        syncRelatedFilterOptions(rememberedContext);
      } else {
        syncRelatedFilterOptions(buildFilterReloadContext(rememberedPath, filterStateRef.current));
      }

      if (isPermalinkPath(rememberedPath) && !rememberedIdentity) {
        void rememberIdentityFromResolvedPath(rememberedPath);
      } else if (isPermalinkPath(rememberedPath)) {
        void getSeoResolveReq({ path: rememberedPath }).then((result) => {
          if (result.status !== 'ok' || getBrowserPathname() !== rememberedPath) {
            return;
          }
          setSeoPage(result.data);
          setSeoPageTitle(result.data.title ?? null);
          applySeoResolveDocumentMeta(result.data);
        });
      } else {
        // Soft SEO pushState keeps the grid mounted — Back to /fanfics|/originals
        // must restore catalog chrome/H1 instead of leaving the permalink seoPage.
        restoreCatalogSeoPage();
      }
    };

    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [
    dispatch,
    filterContextByPathRef,
    filterIdentityByPathRef,
    filterStateRef,
    isPopstateNavigationRef,
    restoreCatalogSeoPage,
    setListingPathEpoch,
    setSeoPage,
    setSeoPageTitle,
    syncRelatedFilterOptions,
    syncStateFromUrl,
  ]);
};
